import type { IAddress } from './Address'
import type { ITenant } from './Tenant'

/** Owner/operator contact block. The single-site detail endpoint nests these here. */
export interface ISiteManagementDetails {
  id?: number
  owner_tenant: ITenant | null
  operator_tenant: ITenant | null
  owner_company?: string | null
  owner_name?: string | null
  owner_email?: string | null
  owner_phone?: string | null
  operator_company?: string | null
  operator_name?: string | null
  operator_email?: string | null
  operator_phone?: string | null
  owner_customer: { id: number; name: string } | null
}

export interface FullSite {
  id: number
  site_id: string
  site_name: string
  verified: boolean
  address: IAddress | null
  type?: string
  lat?: number
  lon?: number
  parcel_id?: string
  owner?: string
  acres?: number
  arrangement?: string
  operator_tenant: ITenant | null
  operator_company?: string
  operator_name?: string
  operator_phone?: string
  operator_email?: string
  owner_tenant: ITenant | null
  owner_company?: string
  owner_name?: string
  owner_phone?: string
  owner_email?: string
  owner_customer: {
    id: number
    name: string
  } | null
  /** Nested owner/operator contact block returned by the single-site detail endpoint. */
  management_details?: ISiteManagementDetails
  status?: string
  notes?: string
  timezone?: string

  // Data Center specific fields
  data_center_type?: string
  infrastructure?: string[]
  cooling_method?: string[]
  purpose?: string[]
  total_capacity?: number
  operational_capacity?: number
  power_source?: {
    type: string
    percentage: number
  }[]
  transmission_provider?: string
  power_provider?: string
  internet_service_provider?: string
  uptime?: number
  iso?: string
  load_resource_zone?: string
  transmission_pricing_zone?: string
  commercial_pricing_node?: string
  elemental_node?: string

  // Repair Center specific fields
  oem_repairs?: boolean
  manufacturers?: string[]
  services?: string[]
}

export interface LiteSite {
  site_id: string
  site_name: string
  type: string | null
  address?: IAddress
  timezone?: string
}

export enum SiteType {
  DataCenter = 'Data Center',
  RepairCenter = 'Repair Center',
  Other = 'Other',
}

export const SiteStatusOptions = ['Planned', 'Decommissioned', 'Operational']
export const SiteArrangementOptions = ['Lease', 'Own', 'Lease-to-Own']
export const SiteDataCenterTypeOptions = ['AI/HPC', 'Crypto']
export const SiteInfrastructureOptions = ['MDC', 'Traditional']
export const SiteCoolingOptions = ['Air', 'Water', 'Immersion']
export const SitePurposeOptions = ['Self Mining', 'Hosting', 'Colocation']
export const SitePowerSourceOptions = [
  'Hydro',
  'Nuclear',
  'Natural Gas',
  'Stranded Natural Gas',
  'Wind',
  'Solar',
  'Coal',
  'Other',
]
export const SiteManufacturerOptions = [
  'Bitmain',
  'MicroBT',
  'Canaan',
  'Innosilicon',
  'Ebang',
  'IceRiver',
  'Auradine',
  'Jasminer',
  'Goldshell',
  'Other',
]
export const SiteRepairServiceOptions = [
  'Ultrasonic Cleaning',
  'Compressed Air Cleaning',
  'Machine Level Repair',
  'Board-Level Repair',
  'ASIC Repair',
  'Training Courses',
  'Other',
]

export enum ISOs {
  CAISO = 'CAISO',
  NYISO = 'NYISO',
  ERCOT = 'ERCOT',
  ISO_NE = 'ISO_NE',
  SPP = 'SPP',
  PJM = 'PJM',
  AESO = 'AESO',
  IESO = 'IESO',
  MISO = 'MISO',
  AEMO = 'AEMO',
}

export const ISOToName = new Map<ISOs, string>([
  [ISOs.CAISO, 'California Independent System Operator (CAISO)'],
  [ISOs.NYISO, 'New York Independent System Operator (NYISO)'],
  [ISOs.ERCOT, 'Electric Reliability Council of Texas (ERCOT)'],
  [ISOs.ISO_NE, 'Independent System Operator New England (ISO-NE)'],
  [ISOs.SPP, 'Southwest Power Pool (SPP)'],
  [ISOs.PJM, 'Pennsylvania-New Jersey-Maryland Interconnection (PJM)'],
  [ISOs.AESO, 'Alberta Electric System Operator (AESO)'],
  [ISOs.IESO, 'Independent Electricity System Operator (IESO)'],
  [ISOs.MISO, 'Midcontinent Independent System Operator (MISO)'],
  [ISOs.AEMO, 'Australian Energy Market Operator (AEMO)'],
])

export const SiteLoadResourceZones = new Map<ISOs, string[]>([
  [ISOs.MISO, ['Z1', 'Z2', 'Z3', 'Z4', 'Z5', 'Z6', 'Z7', 'Z8', 'Z9', 'Z10']],
])

export const SiteTransmissionPricingZones = new Map<ISOs, { name: string; items: string[] }[]>([
  [
    ISOs.MISO,
    [
      {
        name: 'CAZ 1',
        items: [
          'TPZ 08 - Great River Energy (GRE)',
          'TPZ 14 - Minnesota Power (MP)',
          'TPZ 15 - Montana-Dakota Utilities Co. (MDU)',
          'TPZ 16 - Northern States Power Companies (NSP)',
          'TPZ 18 - Otter Tail Power (OTP)',
          'TPZ 20 - Southern Minnesota Municipal Power Agency (SMMPA)',
          'TPZ 26 - Dairyland Power Cooperative (DPC)',
        ],
      },
      {
        name: 'CAZ 2',
        items: [
          'TPZ 2A - ATC - Madison Gas and Electric Company (ATC)',
          'TPZ 2B - ATC - Wisconsin Public Service (ATC-WPS)',
          'TPZ 2C - ATC - Wisconsin Power and Light Company (ATC-WPL)',
          'TPZ 2D - ATC - Wisconsin Electric (ATC-WEC)',
          'TPZ 2E - ATC - Upper Peninsula Power Company (ATC-UPPC)',
        ],
      },
      {
        name: 'CAZ 3',
        items: [
          'TPZ 01 - Alliant Energy West (ALTW)/ITC Midwest (ITCM)',
          'TPZ 24 - MidAmerican Energy Company (MEC)',
          'TPZ 25 - Muscatine Power and Water (MPW)',
        ],
      },
      {
        name: 'CAZ 4',
        items: [
          'TPZ 3A - Ameren, IL (AMIL)',
          'TPZ 07 - City Water, Light & Power - Springfield, IL (CWLP)',
          'TPZ 19 - Southern Illinois Power Cooperative (SIPC)',
        ],
      },
      {
        name: 'CAZ 5',
        items: ['TPZ 3B - Ameren, MO (AMMO)', 'TPZ 06 - City of Columbia, MO (CWLD)'],
      },
      {
        name: 'CAZ 6',
        items: [
          'TPZ 05 - Duke Energy Indiana (DEI/CIN)',
          'TPZ 09 - Hoosier Energy (HE)',
          'TPZ 11 - Indianapolis Power & Light Company (IPL)',
          'TPZ 17 - Northern Indiana Public Service Company (NIPSCO)',
          'TPZ 23 - Vectren Energy (VECT/SIGE)',
          'TPZ 27 - Big Rivers Electric Corporation (BREC)',
        ],
      },
      {
        name: 'CAZ 7',
        items: [
          'TPZ 10 - International Transmission Company (ITC/DECO)',
          'TPZ 13 - Michigan Electric Transmission Company (METC/CONS)',
          'TPZ 13A - Michigan Joint Zone Subzone (MI13A)',
        ],
      },
      {
        name: 'CAZ 8',
        items: ['TPZ 28 - Entergy Arkansas (EATO)'],
      },
      {
        name: 'CAZ 9',
        items: [
          'TPZ 29 - Entergy Louisiana (ELTO)',
          'TPZ 32 - Cleco Power (CLEC)',
          'TPZ 34 - Lafayette City-Parish Consolidated Government (LAFA)',
        ],
      },
      {
        name: 'CAZ 10',
        items: ['TPZ 30 - Entergy Mississippi (EMTO)', 'TPZ 33 - Cooperative Energy (SME)'],
      },
      {
        name: 'CAZ 11',
        items: ['TPZ 31 - Entergy TX (ETTO)'],
      },
      {
        name: 'CAZ 12',
        items: ['TPZ 35 - Entergy New Orleans LLC(ENTO)'],
      },
    ],
  ],
])

export function getISOOptions() {
  return Array.from(ISOToName.entries()).map(([value, label]) => ({ value, label }))
}

export function getLRZOptionsForISO(iso: ISOs) {
  const lrzs = SiteLoadResourceZones.get(iso)
  if (!lrzs) return undefined
  return lrzs.map((key) => ({ value: key, label: key }))
}

export function getTPZOptionsForISO(iso: ISOs) {
  const tpzs = SiteTransmissionPricingZones.get(iso)
  if (!tpzs) return undefined
  return tpzs.map((obj) => ({
    value: obj.name,
    label: obj.name,
    children: obj.items.map((key) => ({ value: key, label: key })),
  }))
}
